import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  CheckCircle,
  XCircle,
  Loader2,
  Key,
  Eye,
  EyeOff,
  ArrowRight,
  Mail
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = searchParams.get("token");
  
  const [status, setStatus] = useState(token ? "form" : "request");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const requestReset = async () => {
    if (!email) {
      toast.error(t('errors.requiredField'));
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setMessage(res.data.message);
      setStatus("sent");
      toast.success(t('auth.passwordResetSent'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error(t('errors.requiredField'));
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error(t('errors.passwordMismatch'));
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error(t('errors.weakPassword'));
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { 
        token, 
        new_password: newPassword 
      });
      setStatus("success");
      toast.success(t('success.updated'));
    } catch (err) {
      setMessage(err.response?.data?.detail || t('errors.generic'));
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      {/* Language Switcher - Top Right */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>

      <Card className="bg-[#0A0A0A] border-white/10 max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === "request" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-[#F7931A]/20 rounded-full flex items-center justify-center">
                <Key className="w-10 h-10 text-[#F7931A]" />
              </div>
              <h2 className="text-2xl font-['Unbounded'] font-bold text-white mb-2">
                {t('auth.forgotPassword')}
              </h2>
              <p className="text-[#94A3B8] mb-6">
                {t('auth.checkYourEmail')}
              </p>
              <div className="space-y-4">
                <div>
                  <Input
                    type="email"
                    placeholder="din@epost.no"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-black/50 border-white/10 text-white"
                  />
                </div>
                <Button 
                  onClick={requestReset}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#FF6B00] to-[#F7931A] text-white"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  {t('auth.resetPassword')}
                </Button>
              </div>
            </>
          )}

          {status === "sent" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-green-500/20 rounded-full flex items-center justify-center">
                <Mail className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-['Unbounded'] font-bold text-white mb-2">
                {t('auth.checkYourEmail')}
              </h2>
              <p className="text-[#94A3B8] mb-6">
                {message || t('auth.passwordResetSent')}
              </p>
              <p className="text-sm text-[#64748B]">
                {t('auth.resendVerification')}
              </p>
            </>
          )}

          {status === "form" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-[#F7931A]/20 rounded-full flex items-center justify-center">
                <Key className="w-10 h-10 text-[#F7931A]" />
              </div>
              <h2 className="text-2xl font-['Unbounded'] font-bold text-white mb-2">
                {t('auth.newPassword')}
              </h2>
              <p className="text-[#94A3B8] mb-6">
                {t('auth.resetPassword')}
              </p>
              <div className="space-y-4 text-left">
                <div>
                  <label className="text-sm text-[#94A3B8] mb-1 block">{t('auth.newPassword')}</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-black/50 border-white/10 text-white pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-[#94A3B8] mb-1 block">{t('auth.confirmPassword')}</label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-black/50 border-white/10 text-white"
                  />
                </div>
                <Button 
                  onClick={resetPassword}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#FF6B00] to-[#F7931A] text-white"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Key className="w-4 h-4 mr-2" />
                  )}
                  {t('auth.resetPassword')}
                </Button>
              </div>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-green-500/20 rounded-full flex items-center justify-center animate-bounce">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-['Unbounded'] font-bold text-white mb-2">
                {t('success.updated')}
              </h2>
              <p className="text-[#94A3B8] mb-6">
                {t('auth.passwordResetSent')}
              </p>
              <Link to="/login">
                <Button className="w-full bg-gradient-to-r from-[#FF6B00] to-[#F7931A] text-white">
                  {t('auth.login')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-red-500/20 rounded-full flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              <h2 className="text-2xl font-['Unbounded'] font-bold text-white mb-2">
                {t('errors.generic')}
              </h2>
              <p className="text-[#94A3B8] mb-6">
                {message || t('errors.sessionExpired')}
              </p>
              <Button 
                onClick={() => setStatus("request")}
                className="w-full bg-gradient-to-r from-[#FF6B00] to-[#F7931A] text-white"
              >
                {t('auth.resetPassword')}
              </Button>
            </>
          )}

          <div className="mt-6 pt-6 border-t border-white/10">
            <Link to="/login" className="text-[#94A3B8] hover:text-white text-sm">
              ← {t('common.back')} {t('auth.login')}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
